import { useTranslation } from "react-i18next";
import { ReactComponent as AlertIcon } from "../../assets/images/icons/ic-attention.svg";
import { ReactComponent as ErrorIcon } from "../../assets/images/icons/ic-close.svg";
import { Each } from "../../common/Each";
import { DialogStatus } from "../../enums";
import Succeeded from "../animated/Succeeded";
import Button from "../Button";
import Loader from "../Loader";
import styles from "./AlertDialog.module.css";
import Dialog from "./Dialog";
import TextInput from "../TextInput";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { ButtonStatus } from "../../common/constants";

const AlertDialog = ({
  open = false,
  appearance = "default",
  dialogProps = {},
  title = "",
  html = "",
  text = "",
  IconComponent = AlertIcon,
  actions = [],
  onClose = () => { },
  status = DialogStatus.Default,
  successMessage = "",
  errorMessage = "",
  node = null
}) => {
  const { t } = useTranslation();
  const [inputValues, setInputValues] = useState({});
  const [disabled, setDisabled] = useState(false);

  const innerActions = useMemo(() =>
    actions.map(action => ({
      ...action,
      disabled: action.confirmText ? true : false,
    })), [actions]
  );

  const handleConfirmTextChange = (value, index, confirmText) => {
    setInputValues(prev => ({ ...prev, [index]: value }));
  };

  const isDisabled = (action, index) =>
    action.confirmText ? inputValues[index] !== action.confirmText : action.disabled;

  return createPortal(
    <Dialog appearance={appearance} open={open} {...dialogProps} onClose={onClose} noHeader style={{ maxWidth: "512px", ...dialogProps?.style }}>
      <div className={`${styles.container} ${appearance === "modern" ? styles.modern : ""}`}>
        {status === DialogStatus.Loading && <div className={styles.icon}><Loader /></div>}
        {status === DialogStatus.Default && <IconComponent className={styles.icon} />}
        {status === DialogStatus.Success && (
          <div className={styles.icon}>
            <Succeeded width="100%" height="100%" />
          </div>
        )}
        {status === DialogStatus.Error && (
          <>
            <ErrorIcon className={styles.icon} style={{ color: "var(--secondary)" }} />
            <div className={styles.title}>{errorMessage || t("dialog.defaultError")}</div>
          </>
        )}
        <div className={styles.title}>{title}</div>
        {text && <div className={styles.text}><Markdown rehypePlugins={[rehypeRaw]}>{text}</Markdown></div>}
        {html && <div className={styles.text}>{html}</div>}
        <Each of={innerActions} render={(action, index) => (
          action.confirmText && (
            <TextInput
              type="text"
              placeholder={`Scrivi '${action.confirmText}' per confermare.`}
              style={{ width: "100%" }}
              onKeyUp={(value) => handleConfirmTextChange(value, index, action.confirmText)}
            />
          )
        )} />

        <div className={styles.actions} style={{ justifyContent: actions.length > 1 ? "space-between" : "center" }}>
          <Each of={innerActions} render={(action, index) => (
            <Button
              status={action.status ?? null}
              accentColor={action.accentColor ?? "var(--primary)"}
              inverse={action.inverse ?? false}
              style={appearance === "modern" ? { padding: "10px 16px", marginTop: 0 } : { padding: ".8rem 2rem", marginTop: "1rem" }}
              onClick={async() => {
                setDisabled(true);
                await action.onClick()
                setDisabled(false);
              }}
              disabled={isDisabled(action, index) || disabled}
            >
              {action.label}
            </Button>
          )} />
        </div>
      </div>
    </Dialog>,
    (node || document.querySelector('.content'))
  );
};

export default AlertDialog;
